import { useEffect, useState, useCallback } from "react";
import { api, formatRupiah, FRANCHISES, FRANCHISE_CLS } from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { CurrencyInput } from "@/components/CurrencyInput";
import { Plus, ClipboardList, Pencil, Trash2, X } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 transition";

const emptyForm = {
  nama_pelanggan: "",
  kontak: "",
  nama_produk: "",
  tcg_franchise: "Pokemon",
  jumlah: 1,
  total_harga: 0,
  dp_nominal: 0,
  status_pembayaran: "dp",
  status_pengiriman: "pending",
  catatan: "",
};

const bayarCls = { dp: "bg-blue-500/10 text-blue-300 border-blue-500/30", lunas: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" };
const kirimCls = {
  pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  dikirim: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  selesai: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

function POModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (editing) setForm({ ...emptyForm, ...editing });
    else setForm(emptyForm);
  }, [editing, open]);

  if (!open) return null;

  const save = async () => {
    if (!form.nama_pelanggan.trim() || !form.nama_produk.trim()) return toast.error("Nama pelanggan & produk wajib diisi");
    setSaving(true);
    const payload = { ...form, jumlah: Number(form.jumlah), total_harga: Number(form.total_harga), dp_nominal: Number(form.dp_nominal) };
    try {
      if (editing) await api.put(`/preorders/${editing.id}`, payload);
      else await api.post("/preorders", payload);
      toast.success(editing ? "Pre-order diperbarui" : "Pre-order dicatat");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-800 bg-[#131822] p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold text-white">{editing ? "Edit Pre-Order" : "Catat Pre-Order"}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Nama Pelanggan</label>
            <input data-testid="po-nama-input" className={inputCls} value={form.nama_pelanggan} onChange={(e) => set("nama_pelanggan", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Kontak (WA/IG)</label>
            <input className={inputCls} value={form.kontak} onChange={(e) => set("kontak", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-400 mb-1.5">Nama Produk</label>
            <input data-testid="po-produk-input" className={inputCls} value={form.nama_produk} onChange={(e) => set("nama_produk", e.target.value)} placeholder="Contoh: Prismatic Evolutions Booster Box" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">TCG / Franchise</label>
            <select className={inputCls} value={form.tcg_franchise} onChange={(e) => set("tcg_franchise", e.target.value)}>
              {FRANCHISES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Jumlah</label>
            <input type="number" className={inputCls} value={form.jumlah} onChange={(e) => set("jumlah", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Total Harga</label>
            <CurrencyInput value={form.total_harga} onChange={(v) => set("total_harga", v)} className={inputCls} data-testid="po-total-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">DP / Uang Muka</label>
            <CurrencyInput value={form.dp_nominal} onChange={(v) => set("dp_nominal", v)} className={inputCls} data-testid="po-dp-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Status Pembayaran</label>
            <select className={inputCls} value={form.status_pembayaran} onChange={(e) => set("status_pembayaran", e.target.value)}>
              <option value="dp">DP</option>
              <option value="lunas">Lunas</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Status Pengiriman</label>
            <select className={inputCls} value={form.status_pengiriman} onChange={(e) => set("status_pengiriman", e.target.value)}>
              <option value="pending">Pending</option>
              <option value="dikirim">Dikirim</option>
              <option value="selesai">Selesai</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-400 mb-1.5">Catatan</label>
            <input className={inputCls} value={form.catatan} onChange={(e) => set("catatan", e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition">Batal</button>
          <button data-testid="save-preorder-button" onClick={save} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-60">
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PreOrders() {
  const [pos, setPos] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/preorders");
    setPos(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const del = async (id) => {
    if (!window.confirm("Hapus pre-order ini?")) return;
    await api.delete(`/preorders/${id}`);
    toast.success("Pre-order dihapus");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Pre-Order"
        subtitle="Pemesanan Produk"
        action={
          <button data-testid="add-preorder-button" onClick={() => { setEditing(null); setModal(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition shadow-lg shadow-indigo-600/30">
            <Plus className="w-4 h-4" /> Catat Pre-Order
          </button>
        }
      />

      {pos.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Belum ada pre-order" subtitle="Catat pesanan pelanggan untuk produk yang akan datang beserta DP-nya." />
      ) : (
        <div className="rounded-xl border border-slate-800 bg-[#131822] overflow-hidden" data-testid="preorders-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="text-left px-5 py-3">Pelanggan</th>
                  <th className="text-left px-5 py-3">Produk</th>
                  <th className="text-right px-5 py-3">Total</th>
                  <th className="text-right px-5 py-3">DP</th>
                  <th className="text-right px-5 py-3">Sisa</th>
                  <th className="text-center px-5 py-3">Bayar</th>
                  <th className="text-center px-5 py-3">Kirim</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {pos.map((p) => (
                  <tr key={p.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition">
                    <td className="px-5 py-3">
                      <p className="text-white font-medium">{p.nama_pelanggan}</p>
                      <p className="text-xs text-slate-500">{p.kontak}</p>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-300">{p.nama_produk} ×{p.jumlah}</span>
                      </div>
                      <Badge className={`mt-1 ${FRANCHISE_CLS[p.tcg_franchise] || FRANCHISE_CLS.Lainnya}`}>{p.tcg_franchise}</Badge>
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-white">{formatRupiah(p.total_harga)}</td>
                    <td className="px-5 py-3 text-right font-mono text-emerald-300">{formatRupiah(p.dp_nominal)}</td>
                    <td className="px-5 py-3 text-right font-mono text-amber-300">{formatRupiah(p.sisa_pembayaran)}</td>
                    <td className="px-5 py-3 text-center"><Badge className={bayarCls[p.status_pembayaran]}>{p.status_pembayaran.toUpperCase()}</Badge></td>
                    <td className="px-5 py-3 text-center"><Badge className={kirimCls[p.status_pengiriman]}>{p.status_pengiriman}</Badge></td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => { setEditing(p); setModal(true); }} data-testid={`edit-po-${p.id}`} className="p-1.5 rounded text-slate-400 hover:text-indigo-300 hover:bg-slate-800"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => del(p.id)} data-testid={`delete-po-${p.id}`} className="p-1.5 rounded text-slate-400 hover:text-rose-300 hover:bg-slate-800"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <POModal open={modal} onClose={() => setModal(false)} onSaved={load} editing={editing} />
    </div>
  );
}
